import './style.scss';

import React, { memo, useState } from 'react';
import { DirectionsRenderer, Marker, Polyline } from 'react-google-maps';
import Map from 'src/components/Map';
import { Button, Icon, Tooltip, useApp, useScreenSize } from 'straightline-ui';
import { MODE } from 'straightline-utils/constants';

const getCenter = (pickup, delivery) => {
  if (pickup || delivery) {
    const center = [];
    if (pickup) {
      center.push(pickup.position);
    }
    if (delivery) {
      center.push(delivery.position);
    }
    return center;
  }

  return { lng: -98.5795, lat: 39.8283 };
};

const Expand = ({ onClick, expanded }) => {
  const { sm } = useScreenSize();
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);

  const handleClick = (event) => {
    toggle();
    return onClick(event);
  };

  return (
    <React.Fragment>
      <Button
        id="mapToggle"
        color="white"
        className="btn-rounded-circle"
        onClick={handleClick}
        style={{
          position: 'absolute',
          left: sm && 'calc(50% - 20px)',
          right: !sm && '0.75rem',
          bottom: '3.2rem',
          zIndex: 1
        }}
      >
        {expanded ? <Icon type="chevron-up" /> : <Icon type="chevron-down" />}
      </Button>
      <Tooltip
        placement="top"
        // Not much we can do about the arrow color bc it is an
        // ::after css content
        innerClassName="bg-white"
        target="mapToggle"
        toggle={toggle}
        isOpen={isOpen}
      >
        {expanded ? 'Collapse Map' : 'Expand Map'}
      </Tooltip>
    </React.Fragment>
  );
};

const DrivingMap = memo(
  ({ quote }) => {
    const app = useApp();

    const { result: route } = app.service('api/shipment-routes').useFind(
      {
        query: {
          pickup_address: quote.pickup_address,
          delivery_address: quote.delivery_address
        }
      },
      {
        refetchOnWindowFocus: false,
        enabled:
          validAddress(quote.pickup_address) &&
          validAddress(quote.delivery_address)
      }
    );

    return (
      <React.Fragment>
        {quote.pickup_address?.position && !route && (
          <Marker
            position={quote.pickup_address.position}
            color="red"
            label={{
              color: '#FFFFFF',
              text: 'A'
            }}
          />
        )}
        {quote.delivery_address?.position && !route && (
          <Marker
            position={quote.delivery_address.position}
            color="red"
            label={{
              color: '#FFFFFF',
              text: 'B'
            }}
          />
        )}

        {route && <DirectionsRenderer directions={route.directions} />}
      </React.Fragment>
    );
  },
  (prevProps, nextProps) => {
    return (
      compareAddress(
        prevProps.quote.pickup_address,
        nextProps.quote.pickup_address
      ) &&
      compareAddress(
        prevProps.quote.delivery_address,
        nextProps.quote.delivery_address
      )
    );
  }
);

const LinesMap = memo(
  ({ quote }) => {
    return (
      <React.Fragment>
        {quote.pickup_address?.position && (
          <Marker
            position={quote.pickup_address.position}
            color="red"
            label={{
              color: '#FFFFFF',
              text: 'A'
            }}
          />
        )}
        {quote.delivery_address?.position && (
          <Marker
            position={quote.delivery_address.position}
            color="red"
            label={{
              color: '#FFFFFF',
              text: 'B'
            }}
          />
        )}
        {quote.pickup_address?.position && quote.delivery_address?.position && (
          <Polyline
            options={{
              geodesic: true,
              path: [
                quote.pickup_address.position,
                quote.delivery_address.position
              ],
              strokeColor: '#4A89F3',
              strokeOpacity: 1,
              strokeWeight: 6
            }}
          />
        )}
      </React.Fragment>
    );
  },
  (prevProps, nextProps) => {
    return (
      compareAddress(
        prevProps.quote.pickup_address,
        nextProps.quote.pickup_address
      ) &&
      compareAddress(
        prevProps.quote.delivery_address,
        nextProps.quote.delivery_address
      )
    );
  }
);

const modeMaps = {
  [MODE.FTL]: DrivingMap,
  [MODE.LTL]: LinesMap,
  [MODE.PARCEL]: LinesMap,
  [MODE.DRAYAGE]: DrivingMap // deprecated
};

export default function MapHeaderQuote({ quote }) {
  const ModeMap = modeMaps[quote.mode];
  const [expanded, setExpanded] = useState(false);
  const center = getCenter(quote.pickup_address, quote.delivery_address);
  const zoom = quote.pickup_address && quote.delivery_address ? undefined : 5;

  return (
    <div className="map-header-quote">
      <Map
        center={center}
        zoom={zoom}
        defaultOptions={{
          zoomControlOptions: {
            // google.maps.ControlPosition.TOP_RIGHT = 3
            position: 3
          }
        }}
        containerClassName={`map-header-container ${expanded ? 'open' : ''}`}
      >
        <ModeMap quote={quote} />
      </Map>
      <Expand expanded={expanded} onClick={() => setExpanded(!expanded)} />
    </div>
  );
}

const addressKeys = ['address_1', 'city', 'state', 'country', 'postal_code'];

const compareAddress = (address1 = {}, address2 = {}) => {
  return addressKeys.every((key) => {
    return address1[key] === address2[key];
  });
};

const validAddress = (address) => {
  if (!address) {
    return false;
  }
  return addressKeys.every((key) => !!address[key]);
};
